import { MarkHearthArch as HearthMark } from "../components/brand/Marks";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Loader2, XCircle, Lock, Download, FileText, CheckCircle2, Circle, MinusCircle } from "lucide-react";
import { shareInfo, shareAccess, shareBundleUrl } from "../lib/api";
import { SharedFuneralResources } from "../components/bereavement/SharedFuneralResources";
import { Button } from "../components/ui/button";
import { AskOnPolicy, ShareThread } from "../components/policies/AuditorComments";
import { Input } from "../components/ui/input";
import { NoIndex } from "../components/Seo";
import { Readout } from "../components/estate/Readout";
import { HospitalShareView } from "../components/share/HospitalShareView";
import { tr, getLang, setLang, useLang } from "../lib/i18n";

const money = (v) => (typeof v === "number" ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—");
const StatusIcon = ({ status }) =>
  status === "ready" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : status === "na" ? <MinusCircle className="h-4 w-4 text-muted-foreground" /> : <Circle className="h-4 w-4 text-amber-500" />;

export default function ShareBundle() {
  const { linkId } = useParams();
  const { lang } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    shareInfo(linkId)
      .then((i) => { if (i.kind === "hospital" && i.lang && i.lang !== getLang() && !sessionStorage.getItem("hv_share_lang_set")) { sessionStorage.setItem("hv_share_lang_set", "1"); setLang(i.lang, { sync: false }); } setInfo(i); })
      .catch((e) => setError(e?.response?.data?.detail || "This share link is not available"));
  }, [linkId]);

  const unlock = async () => {
    setBusy(true);
    try {
      setData(await shareAccess(linkId, code));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect code");
    } finally {
      setBusy(false);
    }
  };

  const isB = info?.kind === "bereavement";
  const isA = info?.kind === "attorney";
  const isP = info?.kind === "policies";
  const isET = info?.kind === "estate_tax";
  const isR = info?.kind === "readout";
  const isH = info?.kind === "hospital";
  const headline = isH ? `${tr("Hospital visit pack for")} ${info?.recipient_name}` : isR ? `A plain-English readout for ${info?.recipient_name}` : isET ? `Estate tax packet for ${info?.recipient_name}` : isP ? `Policy evidence pack for ${info?.recipient_name}` : isA ? `Estate review for ${info?.recipient_name}` : isB ? `Bereavement pack for ${info?.recipient_name}` : `Tax year ${info?.year} for ${info?.recipient_name}`;

  return (
    <div className="min-h-screen bg-background px-6 py-10">
      <NoIndex title="Shared bundle" />
      <Toaster position="top-center" richColors />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="share-page" className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <HearthMark className="h-6 w-6" />
          </div>
          <div>
            <div className="font-serif text-xl font-semibold leading-none">HearthVault</div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mt-1">{isH ? tr("Hospital visit pack") : isR ? "Family readout" : isET ? "Estate tax packet" : isP ? "SOC 2 policy evidence" : isA ? "Attorney review packet" : isB ? "Shared bereavement pack" : "Shared tax bundle"}</div>
          </div>
        </div>

        {!info && !error && <Loader2 className="h-6 w-6 animate-spin text-primary mt-10" />}
        {error && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-8 text-center">
            <XCircle className="h-10 w-10 text-amber-500 mx-auto" />
            <h1 className="mt-4 font-serif text-2xl" data-testid="share-blocked-title">Link unavailable</h1>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {info && !data && (
          <div className="mt-8 rounded-2xl border border-border bg-card p-8 max-w-sm">
            <h1 className="font-serif text-2xl" data-testid="share-title">{headline}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{tr("Shared by")} {info.shared_by || tr("the vault owner")} · {tr("expires")} {new Date(info.expires_at).toLocaleDateString(lang)}</p>
            {isH && <div className="mt-3 inline-flex rounded-full border border-border bg-background p-0.5 text-xs" data-testid="share-lang-toggle">{[["en", "English"], ["es", "Español"]].map(([k, l]) => <button key={k} type="button" onClick={() => setLang(k, { sync: false })} aria-pressed={lang === k} data-testid={`share-lang-${k}`} className={`rounded-full px-3 py-1 ${lang === k ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l}</button>)}</div>}
            <div className="mt-5 flex items-center gap-2">
              <Input data-testid="share-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("6-digit access code")} className="bg-background" onKeyDown={(e) => e.key === "Enter" && unlock()} />
              <Button data-testid="share-unlock-btn" onClick={unlock} disabled={busy || code.length < 6} className="gap-1.5 bg-primary hover:bg-primary/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {tr("Open")}
              </Button>
            </div>
          </div>
        )}

        {data && isH && <HospitalShareView data={data} linkId={linkId} />}

        {data && isA && (
          <div className="mt-8 space-y-6" data-testid="share-content">
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h1 className="font-serif text-2xl">Estate document review</h1>
                <p className="text-sm text-muted-foreground mt-1">From {data.shared_by || "your client"} · library {data.score}% complete · {data.gaps.length} item(s) to review · {data.drafts.length} draft(s)</p>
                {data.note && <p className="mt-2 text-sm rounded-lg bg-muted/60 px-3 py-2" data-testid="share-note">{data.note}</p>}
              </div>
              <Button asChild className="gap-2 bg-primary hover:bg-primary/90 rounded-full">
                <a data-testid="share-download-btn" href={shareBundleUrl(linkId, data.token)}><Download className="h-4 w-4" /> Download packet (.zip)</a>
              </Button>
            </div>
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="share-attorney-general">
              <div className="text-xs uppercase tracking-wider font-mono text-primary mb-1">Your notes for the client</div>
              <p className="text-xs text-muted-foreground mb-2">Leave an overall note here, or comment on a specific item below. Your client is emailed each note and can reply from their vault.</p>
              <ShareThread linkId={linkId} token={data.token} policyId="general" />
              <AskOnPolicy linkId={linkId} token={data.token} policyId="general" author={info?.recipient_name} />
            </div>
            <div className="rounded-2xl border border-border bg-card divide-y divide-border">
              {data.gaps.map((g) => (
                <div key={g.id} data-testid="share-item" className="p-4 flex gap-3">
                  {g.status === "filed" ? <Circle className="h-4 w-4 text-amber-500 mt-0.5" /> : <XCircle className="h-4 w-4 text-rose-500 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">{g.label} <span className="text-[10px] font-mono uppercase text-muted-foreground">{g.status === "filed" ? "filed, not executed" : "missing"}{g.optional ? " · optional" : ""}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{g.what}</div>
                    <div className="text-xs text-muted-foreground mt-1"><b>Signing:</b> {g.who}</div>
                    {(g.executed_on || g.attorney || g.original_location || g.note) && (
                      <div className="text-xs mt-1">{[g.executed_on && `executed ${g.executed_on}`, g.attorney && `attorney ${g.attorney}`, g.original_location && `original at ${g.original_location}`, g.note].filter(Boolean).join(" · ")}</div>
                    )}
                    {g.documents.length > 0 && <ul className="mt-1.5 space-y-1">{g.documents.map((d) => <li key={d.id} className="text-xs text-muted-foreground flex items-center gap-1.5"><FileText className="h-3 w-3" /> {d.title}{d.source === "builder" ? " (client draft)" : ""}</li>)}</ul>}
                    <div className="mt-2"><ShareThread linkId={linkId} token={data.token} policyId={g.id} /><AskOnPolicy linkId={linkId} token={data.token} policyId={g.id} author={info?.recipient_name} /></div>
                  </div>
                </div>
              ))}
            </div>
            {data.drafts.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="share-drafts">
                <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Client drafts in the packet</div>
                <ul className="space-y-1">{data.drafts.map((d) => <li key={d.id} className="text-sm flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-primary" /> {d.title} <span className="text-xs text-muted-foreground">· {d.slot}</span></li>)}</ul>
              </div>
            )}
          </div>
        )}

        {data && isP && (
          <div className="mt-8 space-y-6" data-testid="share-policies">
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h1 className="font-serif text-2xl">Policy adoption evidence</h1>
                <p className="text-sm text-muted-foreground mt-1">{data.signed} of {data.total} policies adopted · set v{data.policy_set_version}, effective {data.effective} · shared by {data.shared_by || "the vault owner"}</p>
              </div>
              <Button asChild className="gap-2 bg-primary hover:bg-primary/90 rounded-full">
                <a data-testid="share-download-btn" href={shareBundleUrl(linkId, data.token)}><Download className="h-4 w-4" /> Download evidence zip</a>
              </Button>
            </div>
            <ul className="space-y-2" data-testid="share-policy-list">
              {data.items.map((p) => (
                <li key={p.id} className="rounded-xl border border-border bg-card px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`share-policy-${p.id}`}>
                  <span className="font-mono text-xs text-primary w-10">{p.id}</span>
                  <span className="font-medium flex-1 min-w-[200px]">{p.title}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${p.signed_at && !p.needs_resign ? "text-emerald-700" : "text-amber-700"}`}>{p.signed_at ? (p.needs_resign ? "re-sign needed" : `signed ${p.signed_at}`) : "not adopted"}</span>
                  <span className="w-full text-xs text-muted-foreground">{p.signed_by ? `${p.signed_by}${p.signer_title ? `, ${p.signer_title}` : ""} · ` : ""}owner {p.owner} · review {p.review.toLowerCase()}{p.review_due ? ` · next ${p.review_due}` : ""}{p.reviewed_at ? ` · last reviewed ${p.reviewed_at}` : ""} · sha256 {p.sha256.slice(0, 12)}…</span>
                  <ShareThread linkId={linkId} token={data.token} policyId={p.id} />
                  <AskOnPolicy linkId={linkId} token={data.token} policyId={p.id} author={info?.recipient_name} />
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">The zip holds each signed policy PDF (current and superseded), review-log.csv and manifest.json with the SHA-256 of every policy text as adopted.</p>
          </div>
        )}

        {data && isR && <Readout data={data} />}
        {data && isET && (
          <div className="mt-8 space-y-6" data-testid="share-estate-tax">
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h1 className="font-serif text-2xl">Estate tax packet (Form 706)</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {data.decedent?.name ? `${data.decedent.name} · ` : ""}Gross estate {money(data.gross_estate)} · taxable {money(data.taxable_estate)} · est. federal tax {money(data.estimated_tax)}
                </p>
              </div>
              <Button asChild className="gap-2 bg-primary hover:bg-primary/90 rounded-full">
                <a data-testid="share-download-btn" href={shareBundleUrl(linkId, data.token)}><Download className="h-4 w-4" /> Download packet (.zip)</a>
              </Button>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                {[["Gross estate", data.gross_estate], ["Total deductions", data.total_deductions], ["Taxable estate", data.taxable_estate], ["Exemption", data.exemption], [`Est. federal tax (≈${data.top_rate_percent}%)`, data.estimated_tax]].map(([l, v]) => (
                  <div key={l} className="flex justify-between gap-3 border-b border-border/40 py-1"><span className="text-muted-foreground">{l}</span><span className="font-mono tabular-nums">{money(v)}</span></div>
                ))}
              </div>
              <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${data.filing_required ? "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" : "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200"}`}>
                {data.filing_required ? "A Form 706 return appears to be required." : "A Form 706 may not be required (gross estate below the exemption)."}
              </div>
              {data.marital_comparison && (
                <p className="mt-2 text-sm" data-testid="share-marital">The marital deduction saves an estimated <b>{money(data.marital_comparison.savings)}</b> in federal estate tax.</p>
              )}
              {data.state_tax && <p className="mt-2 text-xs text-amber-800 dark:text-amber-300" data-testid="share-state-tax">{data.state_tax.note}</p>}
            </div>
            {data.schedules?.length > 0 && (
              <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="share-schedules">
                {data.schedules.map((s) => (
                  <div key={s.id} className="p-3 flex items-center justify-between text-sm">
                    <span>Schedule {s.id} — {s.title} <span className="text-[10px] uppercase font-mono text-muted-foreground">{s.kind}</span></span>
                    <span className="font-mono tabular-nums">{money(s.total)}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{data.disclaimer}</p>
          </div>
        )}

        {data && !isA && !isP && !isET && !isR && !isH && (
          <div className="mt-8 space-y-6" data-testid="share-content">
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h1 className="font-serif text-2xl">{isB ? "When a loved one passes" : `Tax year ${data.year}`}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {isB
                    ? `${data.progress}% of steps ready · shared by ${data.shared_by || "the vault owner"}`
                    : `${data.summary.forms.length} tax forms · deductions ${money(data.summary.deduction_total)}${data.summary.wages ? ` · W-2 wages ${money(data.summary.wages)}` : ""}`}
                </p>
              </div>
              <Button asChild className="gap-2 bg-primary hover:bg-primary/90 rounded-full">
                <a data-testid="share-download-btn" href={shareBundleUrl(linkId, data.token)}>
                  <Download className="h-4 w-4" /> {isB ? "Download pack (.zip)" : "Download bundle (.zip)"}
                </a>
              </Button>
            </div>
            {isB && data.funeral_wishes && (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm" data-testid="share-funeral-wishes">
                <div className="text-[10px] uppercase tracking-widest font-mono text-primary mb-1">Funeral & burial wishes</div>
                {data.funeral_wishes}
              </div>
            )}
            {isB && data.funeral && <SharedFuneralResources f={data.funeral} />}
            {(isB ? data.phases : [{ id: "all", label: null }]).map((ph) => (
              <div key={ph.id}>
                {ph.label && <h2 className="font-serif text-lg mb-2" data-testid={`share-phase-${ph.id}`}>{ph.label}</h2>}
                <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                  {data.items.filter((it) => !isB || it.phase === ph.id).map((it) => (
                    <div key={it.label} data-testid="share-item" className="p-4 flex gap-3">
                      <StatusIcon status={it.status} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{it.label}</div>
                        {it.hint && <div className="text-xs text-muted-foreground">{it.hint}</div>}
                        {it.estate?.contacts?.length > 0 && (
                          <ul className="mt-1.5 space-y-1 text-xs">
                            {it.estate.contacts.map((c) => <li key={c.name + c.role}><span className="font-medium">{c.name}</span> <span className="text-muted-foreground">{[c.role, c.phone, c.email].filter(Boolean).join(" · ")}</span></li>)}
                          </ul>
                        )}
                        {it.documents.length > 0 ? (
                          <ul className="mt-1.5 space-y-1">
                            {it.documents.map((d) => (
                              <li key={d.title + d.date} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <FileText className="h-3 w-3" /> {d.title}
                                {d.amount ? <span className="ml-auto font-mono">{money(d.amount)}</span> : null}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          !it.estate?.contacts?.length && <div className="text-xs text-muted-foreground mt-0.5">{it.status === "na" ? "Not applicable" : "Nothing filed yet"}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
